import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Layers, Sparkles, ArrowUpRight } from "lucide-react";
import Seo, { BREADCRUMB_JSONLD } from "@/components/Seo";
import { aspectSize } from "@/lib/img";

const STUDIO = "https://images.unsplash.com/photo-1617038220319-276d3cfab638?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const VALUES = [
  { icon: ShieldCheck, title: "Discretion by design", body: "Clients and specialists never see each other. Every brief and message is routed and moderated so relationships — and your margin — stay with the studio." },
  { icon: Users, title: "A hand-picked circle", body: "We work with a small, vetted group of jewellery CAD specialists. Applications are reviewed on craft, not volume." },
  { icon: Layers, title: "Production-ready output", body: "You receive clean, manufacturable files — STL, STEP and OBJ — prepared to the deliverables agreed in your quotation." },
  { icon: Sparkles, title: "One transparent price", body: "A single fixed quotation per project. No hourly surprises, no back-and-forth on rates." },
];

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      <Seo
        title="About the Studio"
        description="A managed studio for fine jewellery CAD. We take the brief, agree a fixed price and run the project end to end. Gallery-grade files, no freelancer to manage."
        path="/about"
        jsonLd={[BREADCRUMB_JSONLD([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto grid grid-cols-12 gap-8 px-4 md:px-8 py-16 md:py-28 items-center">
          <div className="col-span-12 md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-6">Our studio</div>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8 tracking-tight">
              A managed studio for <em className="text-primary not-italic">fine jewellery CAD</em>.
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
              GetJewelDesigns is a managed jewellery CAD studio that sits between jewellers who need bespoke CAD work and the specialists who
              produce it. We take the brief, agree a fair fixed price, and manage the project end to end
              — so you get gallery-grade files without ever managing a freelancer.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="aspect-[4/3] overflow-hidden border hairline">
              <img src={STUDIO} {...aspectSize("4/3", 1200)} alt="Jewellery studio" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h2 className="font-serif text-3xl md:text-5xl mb-12">What we stand for.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border hairline">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-background p-8">
                <v.icon className="h-6 w-6 text-primary mb-5" strokeWidth={1} />
                <h3 className="font-serif text-2xl mb-3">{v.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
          <h2 className="font-serif text-3xl md:text-5xl mb-6">Have a piece in mind?</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10">
            Start with a free brief and a transparent quotation. There's no obligation until you approve.
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-xl mx-auto mb-10">
            GetJewelDesigns is a sole proprietorship operated by Sonam Patel, based in Surat, Gujarat, India.
            Reach us at <a href="mailto:getjeweldesigns@gmail.com" className="text-primary hover:underline underline-offset-4">getjeweldesigns@gmail.com</a>.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/categories" className="inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              Explore categories <ArrowUpRight className="h-4 w-4" strokeWidth={1} />
            </Link>
            <Link to="/how-it-works" className="inline-flex items-center gap-3 border hairline px-8 py-4 text-xs uppercase tracking-widest hover:border-primary/60 transition-colors duration-300">
              How it works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
